<template>
  <div class="chart">
    <div class="svg-wrapper">
      <svg ref="svg" overflow="visible" :width="outerWidth" :height="outerHeight">
        <template v-if="width && height">
          <g ref="xAxisGroup"
            :transform="`translate(0, ${lowestY})`">
          </g>

          <g ref="yAxisGroup"
            :transform="`translate(${margin.left}, 0)`">
            <template v-if="config.yAxis && config.yAxis.label">
              <text
                v-for="label in yAxisLabels"
                v-html=label.text
                :key="label.id"
                :x="label.x"
                :y="label.y"
                :transform="`rotate(${label.rotate})`"
                :fill="label.textColor"
                :text-anchor="label.textAnchor"
                :style="`font: ${label.fontWeight} ${label.fontSize}px ${label.fontFamily}`"
              ></text>
            </template>
          </g>

          <!-- TREND LINE -->
          <g
            v-for="trendLine in trendLines"
            :key="trendLine.id">
            <line
              :stroke-dasharray="trendLine.dashArray"
              :x1="trendLine.x1"
              :x2="trendLine.x2"
              :y1="trendLine.y1"
              :y2="trendLine.y2"
              :stroke="trendLine.color"
            />

            <template v-if="trendLine.dotRadius != null">
              <circle
                :cx="trendLine.x1"
                :cy="trendLine.y1"
                :r="trendLine.dotRadius"
                :fill="trendLine.color" />
              <circle
                :cx="trendLine.x2"
                :cy="trendLine.y2"
                :r="trendLine.dotRadius"
                :fill="trendLine.color" />
            </template>
            <template v-else>
              <text
                :x="trendLine.labelX"
                :y="trendLine.labelY"
                :fill="trendLine.textColor"
                :text-anchor="trendLine.textAnchor"
                :style="`font: ${trendLine.fontSize}px ${trendLine.fontFamily}`"
              >{{ trendLine.text }}</text>
            </template>
          </g>

          <!-- OUTLIER -->
          <circle
            v-for="o in outliers"
            :key="`o-${o.x}-${o.y}`"
            :cx="o.x"
            :cy="o.y"
            :r="o.radius"
            :stroke="o.strokeColor"
            :stroke-width="o.strokeWidth"
            fill="none"
            />

          <!-- BOXPLOT -->
          <template v-for="b in boxplots">
            <rect
              :key="`b-${b.box.x}-${b.box.y}`"
              :x="b.box.x"
              :y="b.box.y"
              :width="b.box.width"
              :height="b.box.height"
              :fill="b.box.backgroundColor"
              :stroke="b.box.strokeColor"
              :stroke-width="b.box.strokeWidth"
              />

            <line
              v-if="b.median"
              :key="`mediam-${b.median.y1}`"
              :x1="b.median.x1"
              :y1="b.median.y1"
              :x2="b.median.x2"
              :y2="b.median.y2"
              :stroke="b.median.strokeColor"
              :stroke-width="b.median.strokeWidth"
              />

            <template v-if="b.extreme">
              <template v-if="b.extreme.upper">
                <line
                  :key="`upper-${b.extreme.upper.y1}`"
                  :x1="b.extreme.upper.x1"
                  :y1="b.extreme.upper.y1"
                  :x2="b.extreme.upper.x2"
                  :y2="b.extreme.upper.y2"
                  :stroke="b.extreme.strokeColor"
                  :stroke-width="b.extreme.strokeWidth"
                  />

                <line
                  :key="`center-upper-${b.extreme.centerUpper.y1}`"
                  :x1="b.extreme.centerUpper.x1"
                  :y1="b.extreme.centerUpper.y1"
                  :x2="b.extreme.centerUpper.x2"
                  :y2="b.extreme.centerUpper.y2"
                  :stroke="b.extreme.strokeColor"
                  :stroke-width="b.extreme.strokeWidth"
                  />
              </template>

              <template v-if="b.extreme.lower">
                <line
                  :key="`lower-${b.extreme.lower.y1}`"
                  :x1="b.extreme.lower.x1"
                  :y1="b.extreme.lower.y1"
                  :x2="b.extreme.lower.x2"
                  :y2="b.extreme.lower.y2"
                  :stroke="b.extreme.strokeColor"
                  :stroke-width="b.extreme.strokeWidth"
                  />

                <line
                  :key="`center-lower-${b.extreme.centerLower.y1}`"
                  :x1="b.extreme.centerLower.x1"
                  :y1="b.extreme.centerLower.y1"
                  :x2="b.extreme.centerLower.x2"
                  :y2="b.extreme.centerLower.y2"
                  :stroke="b.extreme.strokeColor"
                  :stroke-width="b.extreme.strokeWidth"
                  />
              </template>

              <circle
                v-if="b.mean"
                :key="`mean-${b.mean.x}-${b.mean.y}`"
                :cx="b.mean.x"
                :cy="b.mean.y"
                :r="b.mean.radius"
                :stroke-width="b.mean.strokeWidth"
                :stroke="b.mean.strokeColor"
                fill="none"
                />
            </template>
          </template>

          <!-- ARROWS -->
          <g v-if="arrow" :transform="`translate(${arrow.x}, 0)`">
            <path
              v-if="arrow.up"
              :d="arrow.up.edgeSymbol"
              :fill="arrow.color"
              :transform="`translate(0, ${arrow.up.edgeY})`" />

            <line
              v-if="arrow.up"
              :x1="0"
              :y1="arrow.up.y1"
              :x2="0"
              :y2="arrow.up.y2"
              :stroke="arrow.color" />

            <text
              v-if="arrow.up"
              text-anchor="middle"
              transform="rotate(90)"
              :style="`font: ${arrow.up.label.font}`"
              :fill="arrow.color"
              :x="arrow.up.label.x"
              :y="arrow.up.label.y">{{ arrow.up.label.text }}</text>

            <circle
              v-if="arrow.dot"
              cx="0"
              :cy="arrow.dot.y"
              :r="arrow.dot.radius"
              :fill="arrow.color" />

            <path
              v-if="arrow.down"
              :d="arrow.down.edgeSymbol"
              :fill="arrow.color"
              :transform="`translate(0, ${arrow.down.edgeY}) rotate(180)`" />

            <line
              v-if="arrow.down"
              :x1="0"
              :y1="arrow.down.y1"
              :x2="0"
              :y2="arrow.down.y2"
              :stroke="arrow.color" />

            <text
              v-if="arrow.down"
              text-anchor="middle"
              transform="rotate(90)"
              :style="`font: ${arrow.down.label.font}`"
              :fill="arrow.color"
              :x="arrow.down.label.x"
              :y="arrow.down.label.y">{{ arrow.down.label.text }}</text>
          </g>
        </template>
      </svg>
    </div>

    <!-- X-AXIS LABEL -->
    <div
      v-if="config.xAxis && config.xAxis.label"
      class="x-label-container"
      :style="`margin-left: ${margin.left}px; margin-right: ${margin.right}px;`">
      <div
        class="x-label"
        v-for="label in xAxisLabels"
        v-html="label.text"
        :key="label.id"
        :style="`font-weight: ${label.fontWeight}; font-size: ${label.fontSize}px; font-family: ${label.fontFamily}; color: ${label.textColor}`" />
    </div>
  </div>
</template>

<script>
import { ref, toRefs } from "@vue/composition-api";

import useBoxplotChartBoxplots from "../composables/BoxplotChart/useBoxplotChartBoxplots";
import useBoxplotChartOutliers from "../composables/BoxplotChart/useBoxplotChartOutliers";
import useBoxplotChartAxes from "../composables/BoxplotChart/useBoxplotChartAxes";
import useChartScales from "../composables/useChartScales";
import useDynamicSizing from "../composables/useDynamicSizing";
import useChartTrendLines from "../composables/useChartTrendLines";
import useChartXAxisLabel from "../composables/useChartXAxisLabel";
import useChartYAxisLabel from "../composables/useChartYAxisLabel";
import useYAxisArrow from "../composables/useYAxisArrow";
import useIPadProDetection from "../composables/useIPadProDetection";

export default {
  name: "BoxplotChart",
  props: {
    data: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    margin: {
      type: Object,
      required: true,
    },
    outerWidth: {
      type: Number,
    },
    outerHeight: {
      type: Number,
    },
  },

  computed: {
    strokeWidth() {
      return this.config?.strokeWidth ?? 1.5;
    },
  },

  setup(props) {
    const { data, margin, config } = toRefs(props);

    // Vue matches the DOM refs based on the returned variable names
    const xAxisGroup = ref(null);
    const yAxisGroup = ref(null);
    const svg = ref(null);

    const { isIPadPro } = useIPadProDetection();
    const { width, height, availableWidth, availableHeight } = useDynamicSizing(svg, margin);
    const { x, y, xBandwidth } = useChartScales(margin, config, availableWidth, availableHeight);
    const { lowestY } = useBoxplotChartAxes(x, y, config, xAxisGroup, yAxisGroup, width, height, isIPadPro);

    const { xAxisLabels } = useChartXAxisLabel(config, availableWidth, margin, isIPadPro);
    const { yAxisLabels } = useChartYAxisLabel(config, availableHeight, margin, isIPadPro);

    const { boxplots } = useBoxplotChartBoxplots(data, config, availableWidth, availableHeight, x, y, xBandwidth, isIPadPro);
    const { outliers } = useBoxplotChartOutliers(data, config, availableWidth, availableHeight, x, y, xBandwidth, isIPadPro);
    const { trendLines } = useChartTrendLines(config, availableWidth, availableHeight, margin, x, y, xBandwidth, isIPadPro);
    const { arrow } = useYAxisArrow(config, margin, availableWidth, availableHeight, x, y, isIPadPro);

    return {
      svg,
      width,
      height,
      lowestY,
      availableWidth,
      availableHeight,
      xAxisGroup,
      yAxisGroup,
      boxplots,
      outliers,
      xAxisLabels,
      yAxisLabels,
      trendLines,
      arrow,
    };
  },
};
</script>

<style lang="scss" scoped>
  .chart {
    display: flex;
    flex-direction: column;
  }

  .x-label-container {
    flex: 0 1 auto;
  }

  .x-label {
    text-align: center;
    line-height: 1;
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .svg-wrapper {
    flex: 1 1 100%;
  }
</style>
